import useLocalStorageState from "use-local-storage-state"
import { useNavigate } from "react-router-dom"
import { useEffect, useState, useRef } from "react"
import { GetRequest, PutRequest, PostRequest } from "../Utils/hooks/ApiHandler#"
import { toast } from "sonner"
import Card from "react-bootstrap/Card"
import { Controller, useForm } from "react-hook-form"
import { Button, Form, Row, Col, Image, Badge } from "react-bootstrap"
import { zodResolver } from "@hookform/resolvers/zod"
import {
    IconCamera,
    IconCircleX,
    IconBuildingSkyscraper,
    IconMapPin,
    IconMailFilled,
    IconWorld,
    IconBriefcaseFilled,
    IconMapPinFilled,
    IconUserFilled,
    IconSettings,
} from "@tabler/icons-react"
import {
    getCities,
    getCountries,
    getStates,
} from "../../src/Utils/hooks/location"
import { z } from "zod"
import Select from "react-select"
import { imageURL } from "../Utils/constants/constant"
import { checkDuplicate, useDebounce } from "../Utils/helpers/commonhelper"
import { faBuilding } from "@fortawesome/free-solid-svg-icons"
import { BsArrowLeft, BsPencil, BsSearch, BsTrash } from "react-icons/bs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

// --- Constants ---
const THEME_COLOR = "#7a3a5e"
const THEME_RGB = "122, 58, 94" // For rgba usage
const THEME_LIGHT_BG = "#fcf4f8"
const THEME_RGBA_SHADOW = "rgba(122, 58, 94, 0.1)"

// --- Schema Definition ---
const setupSchema = z.object({
    company_name: z.string().min(1, "Company name is required"),
    employee_size: z.string(),
    lane1: z.string(),
    lane2: z.string(),
    email: z.string().email("Invalid email"),
    country: z.string(),
    state: z.string(),
    city: z.string(),
    pincode: z.string().min(1, "Zipcode is required"),
    business_domain: z
        .string()
        .url("Invalid URL (include http:// or https://)")
        .or(z.literal(""))
        .optional(),
    industry: z.string().min(1, "Industry is required"),
})

export const SetupCompanyDetails = () => {
    const {
        handleSubmit,
        control,
        register,
        reset,
        setValue,
        watch,
        formState: { errors, isDirty },
    } = useForm({
        resolver: zodResolver(setupSchema),
        mode: "onChange",
        defaultValues: {
            company_name: "",
            employee_size: "",
            lane1: "",
            lane2: "",
            email: "",
            country: "",
            state: "",
            city: "",
            pincode: "",
            business_domain: "",
            industry: "",
        },
    })

    // --- State Management ---
    const [customer, setCustomer] = useLocalStorageState("customer")
    const [setup, setSetup] = useState({})
    const [country, setCountries] = useState([])
    const [states, setStates] = useState([])
    const [city, setCity] = useState([])
    const navigate = useNavigate()

    const fileInputRef = useRef(null)
    const [image, setImage] = useState()
    const [isUploadingImage, setIsUploadingImage] = useState(false)
    const [oldImage, setOldImage] = useState("")
    const [duplicateEmailMessage, setDuplicateEmailMessage] = useState(null)
   
    // Watchers
    const watchedValues = watch()
    const email = watch("email")
    const debouncedEmail = useDebounce(email || "", 500)
    const countryID = watch("country")
    const stateId = watch("state")

    // --- Derived Data for Preview ---
    const selectedCountryName =
        country.find((c) => c.countryId === watchedValues.country)?.countryName ||
        "India"
    const selectedStateName =
        states.find((s) => s.stateCode === watchedValues.state)?.stateName || ""
    const selectedCityName =
        city.find((c) => c.cityCode === watchedValues.city)?.cityName || ""

    // --- Constants ---
    const industryOptions = [
        { value: "", label: "Select Industry" },
        { value: "retail and e-commerce", label: "Retail and e-commerce" },
        { value: "real estate", label: "Real estate" },
        { value: "technology and software", label: "Technology and software" },
        { value: "healthcare", label: "Healthcare" },
        { value: "financial services", label: "Financial Services" },
        { value: "hospitality and travel", label: "Hospitality and Travel" },
        { value: "manufacturing", label: "Manufacturing" },
        { value: "education", label: "Education" },
        { value: "construction", label: "Construction" },
        {
            value: "consulting and professional services",
            label: "Consulting and Professional Services",
        },
        {
            value: "transportation and logistics",
            label: "Transportation and Logistics",
        },
        { value: "non-profit and ngo's", label: "Non-profit and NGOs" },
        { value: "entertainment and media", label: "Entertainment and Media" },
        { value: "agriculture", label: "Agriculture" },
        { value: "energy and utilities", label: "Energy and Utilities" },
        { value: "telecommunications", label: "Telecommunications" },
        { value: "other", label: "Other" },
    ]

    const headers = {
        Authorization: customer?.token?.authorization,
        Authentication: customer?.token?.authentication,
    }
    const rf = customer?.token?.authentication
    const extras = { customer, setCustomer, rf, navigate }
    const customer_id = customer?.customer_details?.customer_id

    // --- Effects ---
    useEffect(() => {
        if (customer?.customer_details?.user_type !== "1") {
            navigate("/dashboard")
        }
    }, [customer, navigate])

    useEffect(() => {
        if (!image) return
        const uploadImage = async () => {
            setIsUploadingImage(true)
            const payload = { id: customer_id, logo: image }
            try {
                const res = await PostRequest(
                    `profile/updateCompany_logo`,
                    payload,
                    true,
                    { headers },
                    extras
                )
                if (res.err) {
                    toast.error("Failed to upload image")
                    return
                }
                toast.success("Image uploaded successfully")
            } catch (error) {
                console.error("Image upload error:", error)
                toast.error("Error uploading image")
            } finally {
                setIsUploadingImage(false)
            }
        }
        uploadImage()
    }, [image])

    useEffect(() => {
        ; (async () => {
            const res = await GetRequest(
                `profile/setup/${customer_id}`,
                { headers },
                { ...extras, rf: headers.Authentication }
            )
            if (res.err) {
                toast.error("Error Fetching Data")
                return
            }
            setSetup(res.data?.[0] || {})
            setOldImage(res.data[0]?.logo || "")
        })()
    }, [customer, customer_id])

    useEffect(() => {
        if (setup && Object.keys(setup).length > 0) {
            reset({
                email: setup?.email || "",
                company_name: setup?.company_name || "",
                employee_size: setup.employee_size || "",
                business_domain: setup.business_domain || "",
                industry: setup.industry || "",
                lane1: setup.address || "",
                lane2: setup.lane2 || "",
                country: setup.country,
                state: setup.state || "",
                city: setup.city || "",
                pincode: setup.pincode || "",
            })
        }
    }, [setup, reset])

    useEffect(() => {
        const checkEmail = async () => {
            if (debouncedEmail) {
                setDuplicateEmailMessage(null)
                if (debouncedEmail === setup.email) return

                const isEmailExisting = await checkDuplicate({
                    col: "email",
                    customer_id: customer_id,
                    validating_val: debouncedEmail,
                    checkFor: "register",
                    contact_id: "",
                })
                if (isEmailExisting) setDuplicateEmailMessage("Email already exists!")
            } else {
                setDuplicateEmailMessage(null)
            }
        }
        checkEmail()
    }, [debouncedEmail, setup.email])

    useEffect(() => {
        ; (async () => {
            const data = await getCountries()
            if (data)
                setCountries(
                    data.map((c) => ({ countryName: c.name, countryId: c.id }))
                )
        })()
    }, [])

    useEffect(() => {
        ; (async () => {
            const data = await getStates(countryID ? countryID : 99)
            setStates(
                data
                    ? data.map((s) => ({ stateName: s.state_name, stateCode: s.id }))
                    : []
            )
        })()
    }, [countryID])

    useEffect(() => {
        ; (async () => {
            if (!stateId) {
                setCity([])
                return
            }
            const data = await getCities(stateId)
            setCity(
                data ? data.map((c) => ({ cityName: c.city_name, cityCode: c.id })) : []
            )
        })()
    }, [stateId])

    const triggerUpload = () => fileInputRef.current.click()

    const onSubmit = async (formData) => {
        if (duplicateEmailMessage) {
            toast.error("Please fix the email error before submitting")
            return
        }
        try {
            const payload = { ...formData, id: customer_id }
            if (isDirty) {
                const res = await PutRequest(
                    "profile/updateSetup/",
                    payload,
                    { headers },
                    extras
                )
                if (res?.err) {
                    toast.error("Something went wrong!")
                    return
                }
                if (res.status === "success") {
                    toast.success("Profile updated successfully!")
                    const updatedRes = await GetRequest(
                        `profile/setup/${customer_id}`,
                        { headers },
                        { ...extras, rf: headers.Authentication }
                    )
                    if (!updatedRes.err) {
                        const response = updatedRes.data?.[0]
                        setSetup(response || {})
                        reset(formData)
                    }
                }
            }
        } catch (error) {
            console.error(error)
            toast.error("Update failed")
        }
    }

    // --- Styles for React Select ---
    const customStyless = {
        control: (provided, state) => ({
            ...provided,
            // CHANGED: Enforce fixed height to prevent jumping on open
            minHeight: "38px",
            height: "38px",
            // CHANGED: Adjust padding to center text within the fixed height
            padding: "0 0.5rem",
            borderRadius: "0.3rem",
            borderColor: state.isFocused ? "#7a3a5eeb" : "#dee2e6",
            boxShadow: state.isFocused ? `0 0 0 1px ${THEME_RGBA_SHADOW}` : "none",
            "&:hover": { borderColor: THEME_COLOR },
        }),
        option: (provided, state) => ({
            // ... (keep existing option logic)
            ...provided,
            backgroundColor: state.isSelected
                ? "#7a3a5eeb"
                : state.isFocused
                    ? THEME_LIGHT_BG
                    : "white",
            color: state.isSelected ? "white" : "#1f2937",
        }),
        singleValue: (provided) => ({
            ...provided,
            color: "#212529",
            fontWeight: 500,
            fontSize: 14,
        }),
        // CHANGED: Ensure the input container doesn't add extra margin
        input: (provided) => ({
            ...provided,
            margin: 0,
            padding: 0,
        }),
        // ... (keep existing menu logic)
        menu: (base) => ({
            ...base,
            zIndex: 9999,
            borderRadius: "0.5rem",
            boxShadow: "0 8px 15px -3px rgba(0, 0, 0, 0.1)",
            fontSize: 14,
        }),
    }

    return (
        <>
            <div className="container">
                {/* Page Header */}
                <div className="mb-3 d-flex justify-content-between align-items-end">
                    <div style={{ lineHeight: "0.8rem" }}>
                        <h6
                            // className="addEnquiryHead"
                            style={{ fontWeight: "400", color: "black", fontSize: "1.13rem" }}
                        >
                            Company Setup
                        </h6>
                        <p className="text-muted mb-0 small">
                            Manage your company profile and branding.
                        </p>
                    </div>
                    <Button className="btn btn-default" onClick={() => navigate(-1)}>
                        <BsArrowLeft className="me-1" /> Back
                    </Button>
                </div>
                <hr
                    className="mt-2 mb-4"
                    style={{
                        borderTop: "0.5px solid #6c757d",
                        opacity: "0.3",
                    }}
                />

                <Form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
                    {/* --- TOP CARD: Identity Preview --- */}
                    <Card className="customBorderforCard" style={{ borderRadius: "6px" }}>
                        {/* Theme Colored Banner */}
                        <div
                        // style={{
                        //   height: "50px",
                        //   // background: `linear-gradient(135deg, ${THEME_COLOR} 0%, rgba(${THEME_RGB}, 0.8) 100%)`,
                        // }}
                        ></div>
                        <Card.Body className="">
                            <div className="d-flex gap-4 flex-column flex-md-row">
                                {/* Logo Section - Overlapping Banner */}
                                <div
                                    className="mx-md-auto mb-3 mb-md-0 position-relative align-self-center align-self-md-start"
                                // style={{ marginTop: "-30px" }}
                                >
                                    <div
                                        className="bg-white p-0 rounded-circle d-inline-block position-relative group-hover-parent"
                                        style={{ width: 150, height: 150 }}
                                    >
                                        <div className="w-100 h-100 rounded-circle overflow-hidden border bg-light d-flex justify-content-center align-items-center position-relative">
                                            {image !== undefined || oldImage ? (
                                                <Image
                                                    src={
                                                        image
                                                            ? URL.createObjectURL(image)
                                                            : `${imageURL}${oldImage}`
                                                    }
                                                    alt="preview"
                                                    className="w-100 h-100"
                                                    style={{ objectFit: "cover" }}
                                                />
                                            ) : (
                                                <div className="text-muted opacity-25">
                                                    <FontAwesomeIcon icon={faBuilding} size="3x" />
                                                </div>
                                            )}

                                            {/* Overlay on Hover */}
                                            {isUploadingImage && (
                                                <div className="position-absolute w-100 h-100 bg-dark bg-opacity-50 d-flex justify-content-center align-items-center">
                                                    <span className="text-white small">Wait...</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Upload Button */}
                                        <Button
                                            variant="light"
                                            onClick={triggerUpload}
                                            disabled={isUploadingImage}
                                            className="position-absolute bottom-0 end-0 rounded-circle shadow border p-0 d-flex justify-content-center align-items-center"
                                            style={{
                                                width: 40,
                                                height: 40,
                                                transform: "translate(-5px, -5px)",
                                            }}
                                        >
                                            <IconCamera size={20} className="text-dark" />
                                        </Button>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            accept="image/*"
                                            onChange={(e) => setImage(e.target.files[0])}
                                            style={{ display: "none" }}
                                        />
                                    </div>
                                </div>

                                {/* Text Content */}
                                <div className="flex-grow-1 pt-3 pt-md-2">
                                    <div className="d-flex flex-column flex-md-column justify-content-between align-items-start">
                                        <div className="me-2 ms-0">
                                            <h2
                                                className=" text-dark mb-2"
                                                style={{
                                                    fontSize: "20px",
                                                    fontWeight: "500",
                                                    color: "#3c3c3c",
                                                }}
                                            >
                                                {watchedValues.company_name || "Company Name"}
                                            </h2>
                                        </div>

                                        {/* Badges Area */}
                                        <div className="d-flex gap-2 flex-wrap mt-1 mt-md-0">
                                            <span
                                                // bg="light"
                                                // text="dark"
                                                className=" d-flex align-items-md-center fw-normal px-2 py-1 border-0 border badge rounded-pill bg-secondary-transparent"
                                                style={{
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                <IconBriefcaseFilled size={14} className="me-1 mb-0" />
                                                {watchedValues.industry || "Industry N/A"}
                                            </span>
                                            <span
                                                // bg="light"
                                                // text="dark"
                                                className=" d-flex align-items-md-center fw-normal px-2 py-1 border-0 border badge rounded-pill bg-secondary-transparent"
                                                style={{
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                <IconUserFilled size={14} className="me-1 mb-0" />
                                                {watchedValues.employee_size
                                                    ? `${watchedValues.employee_size} Employees`
                                                    : "Size N/A"}
                                            </span>
                                        </div>
                                    </div>

                                    <hr className="mt-2 mb-3 opacity-25" />

                                    <Row className="g-1">
                                        {/* Email Section */}
                                        <Col
                                            sm={12}
                                            md={6}
                                            className="mb-2 d-flex align-items-center"
                                        >
                                            <div
                                                className="bg-light rounded-circle me-3 d-flex justify-content-center align-items-center flex-shrink-0"
                                                style={{
                                                    width: 34,
                                                    height: 34,
                                                    border: `1px solid rgb(35 183 229 / 20%)`,
                                                }}
                                            >
                                                <IconMailFilled
                                                    size={15}
                                                    style={{
                                                        color: "rgb(38, 86, 168)",
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <p
                                                    className="mb-0"
                                                    style={{
                                                        fontSize: "12px",
                                                        color: "#B0BAC9",
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    Email
                                                </p>
                                                <p
                                                    className="mb-0"
                                                    style={{
                                                        color: "#2e384d",
                                                        fontWeight: 400,
                                                        fontSize: ".89rem",
                                                    }}
                                                >
                                                    {watchedValues.email || "No email provided"}
                                                </p>
                                            </div>
                                        </Col>
                                        {/* <Col sm={12} md={6}>
                      <div className="d-flex align-items-center text-secondary">
                        <div
                          className="bg-light rounded-circle me-3 d-flex justify-content-center align-items-center flex-shrink-0"
                          style={{
                            width: 34,
                            height: 34,
                            border: `1px solid ${THEME_COLOR}30`,
                          }}
                        >
                          <IconMail size={15} style={{ color: THEME_COLOR }} />
                        </div>
                        <span>Email</span>
                        <div>
                          <span
                            className="text-truncate fs-15"
                            style={{ minWidth: 0 }}
                          >
                            {watchedValues.email || "No email provided"}
                          </span>
                        </div>
                      </div>
                    </Col> */}

                                        {/* Website Section */}

                                        <Col
                                            sm={12}
                                            md={6}
                                            className="mb-2 d-flex align-items-center"
                                        >
                                            <div
                                                className="bg-light rounded-circle me-3 d-flex justify-content-center align-items-center flex-shrink-0"
                                                style={{
                                                    width: 34,
                                                    height: 34,
                                                    border: `1px solid rgb(35 183 229 / 20%)`,
                                                }}
                                            >
                                                <IconWorld
                                                    size={15}
                                                    style={{ color: "rgb(38, 86, 168)" }}
                                                />
                                            </div>
                                            <div>
                                                <p
                                                    className="mb-0"
                                                    style={{
                                                        fontSize: "12px",
                                                        color: "#B0BAC9",
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    Website
                                                </p>
                                                <p
                                                    className="mb-0"
                                                    style={{
                                                        color: "#2e384d",
                                                        fontWeight: 400,
                                                        fontSize: ".89rem",
                                                    }}
                                                >
                                                    <a
                                                        href={watchedValues.business_domain}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-decoration-none text-dark"
                                                    >
                                                        {watchedValues.business_domain || "No website URL"}
                                                    </a>
                                                </p>
                                            </div>
                                        </Col>
                                        {/* <Col sm={12} md={6}>
                      <div className="d-flex align-items-center text-secondary">
                        <div
                          className="bg-light rounded-circle me-3 d-flex justify-content-center align-items-center flex-shrink-0"
                          style={{
                            width: 34,
                            height: 34,
                            border: `1px solid ${THEME_COLOR}30`,
                          }}
                        >
                          <IconWorld size={15} style={{ color: THEME_COLOR }} />
                        </div>
                        <div
                          className="text-truncate fs-15"
                          style={{ minWidth: 0 }}
                        >
                          <a
                            href={watchedValues.business_domain}
                            target="_blank"
                            rel="noreferrer"
                            className="text-decoration-none text-secondary"
                          >
                            {watchedValues.business_domain || "No website URL"}
                          </a>
                        </div>
                      </div>
                    </Col> */}

                                        {/* Location Section */}
                                        <Col sm={12} className="mb-2 d-flex align-items-center">
                                            <div
                                                className="bg-light rounded-circle me-3 d-flex justify-content-center align-items-center flex-shrink-0"
                                                style={{
                                                    width: 34,
                                                    height: 34,
                                                    border: `1px solid rgb(35 183 229 / 20%)`,
                                                }}
                                            >
                                                <IconMapPinFilled
                                                    size={15}
                                                    style={{ color: "rgb(38, 86, 168)" }}
                                                />
                                            </div>
                                            <div>
                                                <p
                                                    className="mb-0"
                                                    style={{
                                                        fontSize: "12px",
                                                        color: "#B0BAC9",
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    Address
                                                </p>
                                                <p
                                                    className="mb-0"
                                                    style={{
                                                        color: "#2e384d",
                                                        fontWeight: 400,
                                                        fontSize: ".89rem",
                                                    }}
                                                >
                                                    {[
                                                        selectedCityName,
                                                        selectedStateName,
                                                        selectedCountryName,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(", ") || "Location not set"}
                                                </p>
                                            </div>
                                        </Col>
                                        {/* <Col md={12}>
                      <div className="d-flex align-items-center text-secondary">
                        <div
                          className="bg-light rounded-circle me-3 d-flex justify-content-center align-items-center flex-shrink-0"
                          style={{
                            width: 34,
                            height: 34,
                            border: `1px solid ${THEME_COLOR}30`,
                          }}
                        >
                          <IconMapPin
                            size={15}
                            style={{ color: THEME_COLOR }}
                          />
                        </div>
                        <span
                          className="text-truncate fs-15"
                          style={{ minWidth: 0 }}
                        >
                          {[
                            selectedCityName,
                            selectedStateName,
                            selectedCountryName,
                          ]
                            .filter(Boolean)
                            .join(", ") || "Location not set"}
                        </span>
                      </div>
                    </Col> */}
                                    </Row>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* <hr className="my-2 opacity-25" /> */}

                    {/* --- BOTTOM CARD: Edit Form --- */}
                    <Row>
                        <Col md={9}>
                            <Card
                                className="customBorderforCard"
                                style={{ borderRadius: "6px" }}
                            >
                                <Card.Body className="p-4 p-md-4">
                                    {/* Section 1 */}
                                    <div className="mb-5">
                                        <div className="d-flex align-items-center justify-content-between mb-4">
                                            {/* Left Side: Icon + Text */}
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="bg-light p-2 rounded-circle me-3"
                                                    style={{ border: "1px solid rgb(35 183 229 / 20%)" }}
                                                >
                                                    <IconBuildingSkyscraper
                                                        size={24}
                                                        style={{
                                                            color: "rgb(35 183 229 / 97%)",
                                                        }}
                                                    />
                                                </div>
                                                <div>
                                                    <h4
                                                        className="mb-0"
                                                        style={{
                                                            fontSize: "18px",
                                                            fontWeight: "500",
                                                        }}
                                                    >
                                                        Organization Details
                                                    </h4>
                                                    <small className="text-muted">
                                                        Basic identity and classification
                                                    </small>
                                                </div>
                                            </div>

                                            {/* Right Side: Badge */}
                                            <div>
                                                {isDirty && (
                                                    <div className="animate__animated animate__fadeIn">
                                                        <Badge
                                                            bg="warning"
                                                            text="dark"
                                                            className="px-3 py-2 rounded-pill fw-normal shadow-sm border border-warning"
                                                        >
                                                            Unsaved Changes
                                                        </Badge>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <Row className="g-4">
                                            <Col md={6} className="mb-1">
                                                <Form.Group controlId="company_name">
                                                    <Form.Label>
                                                        <p className="fs-14 text-black">
                                                            Company Name{" "}
                                                            <span className="text-danger">*</span>
                                                        </p>
                                                        <p
                                                            className="text-muted mb-1"
                                                            style={{ fontSize: "0.72rem", fontWeight: "300" }}
                                                        >
                                                            Enter your registered company full name
                                                        </p>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="e.g. Acme Corp"
                                                        {...register("company_name")}
                                                        isInvalid={!!errors?.company_name}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors?.company_name?.message}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6} className=" mb-1">
                                                <Form.Group controlId="employee_size">
                                                    <Form.Label>
                                                        <p className="fs-14 text-black">
                                                            Company Size{" "}
                                                            <span className="text-danger">*</span>
                                                        </p>
                                                        <p
                                                            className="text-muted mb-1 small"
                                                            style={{ fontSize: "0.72rem", fontWeight: "300" }}
                                                        >
                                                            Select your company head-count approximate range
                                                        </p>
                                                    </Form.Label>
                                                    <Form.Select
                                                        {...register("employee_size")}
                                                        style={{ borderRadius: "4px" }}
                                                    >
                                                        <option value="">Select size...</option>
                                                        <option value="self-employed">Self-employed</option>
                                                        <option value="1-10">1-10 employees</option>
                                                        <option value="11-50">11-50 employees</option>
                                                        <option value="51-200">51-200 employees</option>
                                                        <option value="201-500">201-500 employees</option>
                                                        <option value="501-1000">501-1000 employees</option>
                                                        <option value="1001-5000">
                                                            1001-5000 employees
                                                        </option>
                                                        <option value="10,001+">10,001+ employees</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6} className=" mb-1">
                                                <Form.Group controlId="email">
                                                    <Form.Label>
                                                        <p className="fs-14 text-black">
                                                            Official Email{" "}
                                                            <span className="text-danger">*</span>
                                                        </p>
                                                        <p
                                                            className="text-muted mb-1 small"
                                                            style={{ fontSize: "0.72rem", fontWeight: "300" }}
                                                        >
                                                            Enter your official communication email address
                                                            for updates and notifications
                                                        </p>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="email"
                                                        placeholder="contact@company.com"
                                                        {...register("email")}
                                                        isInvalid={
                                                            !!errors?.email || !!duplicateEmailMessage
                                                        }
                                                    />
                                                    {duplicateEmailMessage && (
                                                        <div className="text-danger small mt-1">
                                                            <IconCircleX size={14} /> {duplicateEmailMessage}
                                                        </div>
                                                    )}
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors?.email?.message}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6} className=" mb-1">
                                                <Form.Group controlId="business_domain">
                                                    <Form.Label>
                                                        <p className="fs-14 text-black">
                                                            Website URL <span className="text-danger">*</span>
                                                        </p>
                                                        <p
                                                            className="text-muted mb-1 small"
                                                            style={{ fontSize: "0.72rem", fontWeight: "300" }}
                                                        >
                                                            Enter your business website which will be appear
                                                            in your company profile
                                                        </p>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="https://www.company.com"
                                                        {...register("business_domain")}
                                                        isInvalid={!!errors?.business_domain}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors?.business_domain?.message}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col md={12} className=" mb-1">
                                                <Form.Group controlId="industry">
                                                    <Form.Label>
                                                        <p className="fs-14 text-black">
                                                            Industry <span className="text-danger">*</span>
                                                        </p>
                                                        <p
                                                            className="text-muted mb-1 small"
                                                            style={{ fontSize: "0.72rem", fontWeight: "300" }}
                                                        >
                                                            Select your best suitable industry matches with
                                                            your line of business
                                                        </p>
                                                    </Form.Label>
                                                    <Controller
                                                        name="industry"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                {...field}
                                                                options={industryOptions}
                                                                value={
                                                                    industryOptions.find(
                                                                        (opt) => opt.value === field.value
                                                                    ) || null
                                                                }
                                                                onChange={(val) =>
                                                                    field.onChange(val?.value || "")
                                                                }
                                                                styles={customStyless}
                                                                placeholder="Select Industry..."
                                                                isClearable
                                                            />
                                                        )}
                                                    />
                                                    {errors?.industry && (
                                                        <p className="text-danger small mt-1">
                                                            {errors.industry.message}
                                                        </p>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            {/* <Col md={12}>
                    <div>
                      <input class="form-input-check" type="checkbox" />
                      <p>CHeckbox For Industry</p>
                    </div>
                  </Col> */}
                                        </Row>
                                    </div>
                                    <hr />
                                    {/* Section 2 */}
                                    <div className="mb-3">
                                        <div className="d-flex align-items-center mb-4">
                                            <div
                                                className="bg-light p-2 rounded-circle me-3"
                                                style={{ border: "1px rgb(35 183 229 / 20%) solid" }}
                                            >
                                                <IconMapPin
                                                    size={24}
                                                    style={{ color: "rgb(35 183 229 / 97%)" }}
                                                />
                                            </div>
                                            <div>
                                                <h5
                                                    className="mb-0"
                                                    style={{
                                                        fontSize: "18px",
                                                        fontWeight: "500",
                                                    }}
                                                >
                                                    Location & Address
                                                </h5>
                                                <small className="text-muted">
                                                    Headquarters and billing address
                                                </small>
                                            </div>
                                        </div>

                                        <Row className="g-4  mb-1">
                                            <Col md={6} className=" mb-1">
                                                <Form.Group controlId="country">
                                                    <Form.Label>
                                                        <p className="fs-14 text-black">
                                                            Country <span className="text-danger">*</span>
                                                        </p>
                                                        <p
                                                            className="text-muted mb-1 small"
                                                            style={{ fontSize: "0.72rem", fontWeight: "300" }}
                                                        >
                                                            Select your native county where company registered
                                                        </p>
                                                    </Form.Label>
                                                    <Controller
                                                        name="country"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                options={[
                                                                    { value: "99", label: "India" },
                                                                    ...country.map((c) => ({
                                                                        value: c.countryId,
                                                                        label: c.countryName,
                                                                    })),
                                                                ]}
                                                                value={
                                                                    watch("country")
                                                                        ? {
                                                                            value: watch("country"),
                                                                            label:
                                                                                country.find(
                                                                                    (c) =>
                                                                                        c.countryId === watch("country")
                                                                                )?.countryName || "India",
                                                                        }
                                                                        : { value: "99", label: "India" }
                                                                }
                                                                onChange={(opt) => {
                                                                    setValue("country", opt ? opt.value : "99")
                                                                    setValue("state", "")
                                                                    setValue("city", "")
                                                                }}
                                                                styles={customStyless}
                                                            />
                                                        )}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6} className=" mb-1">
                                                <Form.Group controlId="state">
                                                    <Form.Label>
                                                        <p className="fs-14 text-black">
                                                            State <span className="text-danger">*</span>
                                                        </p>
                                                        <p
                                                            className="text-muted mb-1 small"
                                                            style={{ fontSize: "0.72rem", fontWeight: "300" }}
                                                        >
                                                            Select your state of the country
                                                        </p>
                                                    </Form.Label>
                                                    <Controller
                                                        name="state"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                styles={customStyless}
                                                                options={states.map((s) => ({
                                                                    value: s.stateCode,
                                                                    label: s.stateName,
                                                                }))}
                                                                value={
                                                                    states.find(
                                                                        (s) => s.stateCode === field.value
                                                                    )
                                                                        ? {
                                                                            value: field.value,
                                                                            label: states.find(
                                                                                (s) => s.stateCode === field.value
                                                                            )?.stateName,
                                                                        }
                                                                        : null
                                                                }
                                                                onChange={(opt) => {
                                                                    field.onChange(opt ? opt.value : "")
                                                                    setValue("city", "")
                                                                }}
                                                                placeholder="Select State"
                                                                isClearable
                                                            />
                                                        )}
                                                    />
                                                    {errors?.state && (
                                                        <p className="text-danger small mt-1">
                                                            {errors.state.message}
                                                        </p>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={6} className=" mb-1">
                                                <Form.Group controlId="city">
                                                    <Form.Label>
                                                        <p className="fs-14 text-black">
                                                            City <span className="text-danger">*</span>
                                                        </p>
                                                        <p
                                                            className="text-muted mb-1 small"
                                                            style={{ fontSize: "0.72rem", fontWeight: "300" }}
                                                        >
                                                            Select your native city
                                                        </p>
                                                    </Form.Label>
                                                    <Controller
                                                        name="city"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                styles={customStyless}
                                                                options={city.map((c) => ({
                                                                    value: c.cityCode,
                                                                    label: c.cityName,
                                                                }))}
                                                                value={
                                                                    city.find((c) => c.cityCode === field.value)
                                                                        ? {
                                                                            value: field.value,
                                                                            label: city.find(
                                                                                (c) => c.cityCode === field.value
                                                                            )?.cityName,
                                                                        }
                                                                        : null
                                                                }
                                                                onChange={(opt) =>
                                                                    field.onChange(opt?.value || "")
                                                                }
                                                                placeholder="Select City"
                                                                isClearable
                                                            />
                                                        )}
                                                    />
                                                    {errors?.city && (
                                                        <p className="text-danger small mt-1">
                                                            {errors.city.message}
                                                        </p>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={6} className=" mb-1">
                                                <Form.Group controlId="pincode">
                                                    <Form.Label>
                                                        <p className="fs-14 text-black">
                                                            Zipcode <span className="text-danger">*</span>
                                                        </p>
                                                        <p
                                                            className="text-muted mb-1 small"
                                                            style={{ fontSize: "0.72rem", fontWeight: "300" }}
                                                        >
                                                            Enter zip or pin code of your company location
                                                        </p>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        {...register("pincode")}
                                                        isInvalid={!!errors?.pincode}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors?.pincode?.message}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col md={12} className=" mb-0">
                                                <Form.Group controlId="lane1">
                                                    <Form.Label>
                                                        {" "}
                                                        <p className="fs-14 text-black">
                                                            Address <span className="text-danger">*</span>
                                                        </p>
                                                        <p
                                                            className="text-muted mb-1 small"
                                                            style={{ fontSize: "0.72rem", fontWeight: "300" }}
                                                        >
                                                            Enter address of your company.
                                                        </p>
                                                    </Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        placeholder="Address Line 1"
                                                        // rows={2}
                                                        {...register("lane1")}
                                                        isInvalid={!!errors?.lane1}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12} className=" mb-1">
                                                <Form.Group controlId="lane2">
                                                    {/* <Form.Label>
                            {" "}
                            {/* <p className="fs-14 text-black">
                              Address Line 2
                              <span className="text-danger">*</span>
                            </p>
                            <p
                              className="text-muted mb-1 small"
                              style={{ fontSize: "0.72rem", fontWeight: "300" }}
                            >
                              Manage your company profile and branding. auhe
                            </p> */}
                                                    {/* </Form.Label> */}
                                                    <Form.Control
                                                        as="textarea"
                                                        placeholder="Address Line 2"
                                                        // rows={2}
                                                        {...register("lane2")}
                                                        isInvalid={!!errors?.lane2}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="d-flex justify-content-end pt-4 mt-3 border-top">
                                        {/* <Button
                  variant="light"
                  className="me-2 text-muted px-4"
                  onClick={() => reset()}
                >
                  Cancel
                </Button> */}
                                        <Button
                                            type="submit"
                                            className="px-3 fw-medium btn btn-default"
                                            disabled={!!duplicateEmailMessage || isUploadingImage}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            {/* <Card className="custom-card" style={{ background: "#b28274" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <img
                    src={
                      "https://cdn-icons-png.flaticon.com/512/3476/3476376.png"
                    }
                    style={{
                      width: "66px",
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: "8px",
                    }}
                  />
                </div>
                <h5
                  className="fw-bold mb-2"
                  style={{
                    color: "#ffffff",
                    lineHeight: "1.6",
                    fontSize: "23px",
                  }}
                >
                  Upgrade to a Team plan
                </h5>
                <p class="mb-4" style={{ color: "#ffffff" }}>
                  Start collaborating with your colleagues and customers in
                  real-time.
                </p>
                <button
                  class="btn btn-default fs-16"
                  style={{
                    fontSize: "14px !important",
                    background: "#ffd7ab !important",
                    border: "1px solid #ffd7ab !important",
                    color: "#5c1b3d !important",
                  }}
                >
                  Upgrade
                </button>
              </Card> */}
                            <Card
                                className="custom-card"
                                style={{
                                    background: "linear-gradient(240deg, #ffd7ab, transparent)",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        width: "100%",
                                    }}
                                >
                                    <img
                                        src={
                                            "https://cdn-icons-png.flaticon.com/512/3476/3476376.png"
                                        }
                                        style={{
                                            width: "66px",
                                            display: "flex",
                                            justifyContent: "center",
                                            marginBottom: "16px",
                                            marginTop: "16px",
                                        }}
                                    />
                                </div>
                                <h5
                                    className="fw-bold mb-2"
                                    style={{
                                        color: "#2d2a52",
                                        lineHeight: "1.6",
                                        fontSize: "23px",
                                    }}
                                >
                                    Upgrade to a Team plan
                                </h5>
                                <p className="mb-4">
                                    Start collaborating with your colleagues and customers in
                                    real-time.
                                </p>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault()
                                    }}
                                    className="btn btn-default fs-16"
                                    style={{ fontSize: "14px !important" }}
                                >
                                    Upgrade
                                </button>
                            </Card>
                        </Col>
                    </Row>
                </Form>
            </div>

            <style jsx>{`
        /* --- Theme Colors --- */

        .css-1xc3v61-indicatorContainer {
          display: -ms-flexbox;
          display: flex;
          transition: color 150ms;
          color: hsl(0, 0%, 80%);
          padding: 5px;
          box-sizing: border-box;
        }

        .css-1u9des2-indicatorSeparator {
          -ms-flex-item-align: stretch;
          align-self: stretch;
          width: 1px;
          background-color: hsl(0, 0%, 80%);
          margin-bottom: 3px;
          margin-top: 3px;
          box-sizing: border-box;
        }

        // .text-theme {
        //   color: ${THEME_COLOR} !important;
        // }

        // .bg-theme-subtle {
        //   background-color: rgba(${THEME_RGB}, 0.5) !important;
        // }

        // .btn-theme {
        //   background-color: ${THEME_COLOR} !important;
        //   border-color: ${THEME_COLOR} !important;
        //   color: white !important;
        // }

        // .btn-theme:hover {
        //   background-color: #612d4a !important; /* Slightly darker shade */
        //   border-color: #612d4a !important;
        // }

        /* --- Form Inputs Focus States --- */
        // .form-control:focus,
        // .form-select:focus {
        //   border-color: ${THEME_COLOR} !important;
        //   box-shadow: 0 0 0 4px rgba(${THEME_RGB}, 0.1) !important;
        // }

        .form-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #4b5563;
          margin-bottom: 0.4rem;
        }
        .customBorderforCard {
          border: 1px solid #d4e9f0 !important;
        }
      `}</style>
        </>
    )
}
